import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import {
  FaBars,
  FaBell,
  FaHome,
  FaBuilding,
  FaUsers,
  FaMoneyBillWave,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaUserCircle,
  FaSyncAlt,
} from "react-icons/fa";

import API from "../config/API";

function ApplicationHeadDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    appExecutorsCount: 0,
    basicPlanPgOwnersCount: 0,
    paidPlanUsersCount: 0,
    pgOwnersCount: 0,
    todayAddedUsersUpdate: 0,
    totalRecievedAmount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const appAdminEmailId =
    loggedUser?.emailId || loggedUser?.appAdminEmailId || "";

  useEffect(() => {
    if (appAdminEmailId) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [appAdminEmailId]);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const response = await axios.get(`${API.V1}/head/getAppAdminDashboard`, {
        params: {
          appAdminEmailId: appAdminEmailId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.jwtToken}`,
        },
      });

      if (response.data?.statusCode === 200) {
        const data = response.data?.data || {};

        setDashboardData({
          appExecutorsCount: data.appExecutorsCount ?? 0,
          basicPlanPgOwnersCount: data.basicPlanPgOwnersCount ?? 0,
          paidPlanUsersCount: data.paidPlanUsersCount ?? 0,
          pgOwnersCount: data.pgOwnersCount ?? 0,
          todayAddedUsersUpdate: data.todayAddedUsersUpdate ?? 0,
          totalRecievedAmount: data.totalRecievedAmount ?? 0,
        });
      } else {
        console.error(
          response.data?.message || "Unable to fetch dashboard data",
        );
      }
    } catch (error) {
      console.error("Dashboard API Error:", error);

      if (error.response?.status === 401) {
        alert("Session Expired. Please login again.");

        localStorage.clear();
        navigate("/");

        return;
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const formatAmount = (amount) => {
    const numericAmount = Number(amount) || 0;

    return numericAmount.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const menus = [
    {
      id: "dashboard",
      name: "Dashboard",
      icon: <FaHome />,
      path: "/applicationHeadDashboard",
    },
    {
      id: "pgOwners",
      name: "PG Owners",
      icon: <FaBuilding />,
      path: "/pgOwnersData",
    },
    {
      id: "executors",
      name: "App Executors",
      icon: <FaUsers />,
      path: "/executorList",
    },
    {
      id: "payments",
      name: "Payments",
      icon: <FaMoneyBillWave />,
      path: "/payments",
    },
    {
      id: "reports",
      name: "Reports",
      icon: <FaChartBar />,
      path: "/reports",
    },
    {
      id: "settings",
      name: "Settings",
      icon: <FaCog />,
      path: "/settings",
    },
    {
      id: "logout",
      name: "Logout",
      icon: <FaSignOutAlt />,
      path: "/login",
    },
  ];

  return (
    <div style={styles.dashboard}>
      {/* ================= SIDEBAR ================= */}

      <div
        style={{
          ...styles.sidebar,
          width: sidebarOpen ? 250 : 80,
        }}
      >
        <div style={styles.logo}>
          {sidebarOpen ? "🏨 Application Owner" : "🏨"}
        </div>

        {menus.map((menu) => (
          <div
            key={menu.id}
            onClick={() => {
              if (menu.id === "logout") {
                handleLogout();
              } else {
                navigate(menu.path);
              }
            }}
            style={{
              ...styles.menu,
              background:
                location.pathname === menu.path ? "#2563EB" : "transparent",
              color: location.pathname === menu.path ? "#fff" : "#E5E7EB",
            }}
          >
            <span style={styles.menuIcon}>{menu.icon}</span>

            {sidebarOpen && <span style={{ marginLeft: 12 }}>{menu.name}</span>}
          </div>
        ))}
      </div>

      {/* ================= MAIN ================= */}

      <div style={styles.main}>
        {/* ================= NAVBAR ================= */}

        <div style={styles.navbar}>
          <div style={styles.left}>
            <FaBars
              size={22}
              style={{
                cursor: "pointer",
              }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            />

            <h2 style={styles.navTitle}>Application Head Dashboard</h2>
          </div>

          <div style={styles.right}>
            <div style={styles.notification}>
              <FaBell size={22} />

              <span style={styles.badge}>5</span>
            </div>

            <FaUserCircle size={34} color="#2563EB" />

            <span style={styles.userName}>Application Head</span>
          </div>
        </div>

        {/* ================= CONTENT ================= */}

        <div style={styles.content}>
          <div style={styles.headerSection}>
            <div>
              <h2 style={styles.welcome}>Welcome 👋</h2>

              <p style={styles.description}>
                Monitor your complete PG Management System from one place.
              </p>
            </div>

            <button
              onClick={fetchDashboardData}
              disabled={refreshing}
              style={styles.refreshButton}
            >
              <FaSyncAlt
                style={{
                  marginRight: 8,
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                }}
              />

              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {/* ================= CARDS ================= */}

          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.loader}></div>
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <div style={styles.cardsGrid}>
              {/* TOTAL PG OWNERS */}

              <div
                style={styles.card}
                onClick={() => navigate("/pgOwnersData")}
              >
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#DBEAFE",
                    color: "#2563EB",
                  }}
                >
                  <FaBuilding size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>Total PG Owners</p>

                  <h1 style={styles.cardValue}>
                    {dashboardData.pgOwnersCount}
                  </h1>

                  <p style={styles.cardDescription}>Click to Manage</p>
                </div>
              </div>

              {/* EXECUTORS */}

              <div
                style={styles.card}
                onClick={() => navigate("/executorList")}
              >
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#DCFCE7",
                    color: "#16A34A",
                  }}
                >
                  <FaUsers size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>App Executors</p>

                  <h1 style={styles.cardValue}>
                    {dashboardData.appExecutorsCount}
                  </h1>

                  <p style={styles.cardDescription}>Click to Manage</p>
                </div>
              </div>

              {/* BASIC PLAN PG OWNERS */}

              <div style={styles.card}>
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#FEF3C7",
                    color: "#D97706",
                  }}
                >
                  <FaBuilding size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>Basic Plan PG Owners</p>

                  <h1 style={styles.cardValue}>
                    {dashboardData.basicPlanPgOwnersCount}
                  </h1>

                  <p style={styles.cardDescription}>Active Basic Plan</p>
                </div>
              </div>

              {/* PAID PLAN USERS */}

              <div style={styles.card}>
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#EDE9FE",
                    color: "#7C3AED",
                  }}
                >
                  <FaUsers size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>Paid Plan Users</p>

                  <h1 style={styles.cardValue}>
                    {dashboardData.paidPlanUsersCount}
                  </h1>

                  <p style={styles.cardDescription}>Premium Users</p>
                </div>
              </div>

              {/* TODAY USERS */}

              <div style={styles.card}>
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#FCE7F3",
                    color: "#DB2777",
                  }}
                >
                  <FaUsers size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>Today's Added Users</p>

                  <h1 style={styles.cardValue}>
                    {dashboardData.todayAddedUsersUpdate}
                  </h1>

                  <p style={styles.cardDescription}>Today's registrations</p>
                </div>
              </div>

              {/* TOTAL RECEIVED AMOUNT */}

              <div style={styles.card} onClick={() => navigate("/payments")}>
                <div
                  style={{
                    ...styles.iconBox,
                    background: "#CCFBF1",
                    color: "#0F766E",
                  }}
                >
                  <FaMoneyBillWave size={25} />
                </div>

                <div style={styles.cardContent}>
                  <p style={styles.cardTitle}>Total Received Amount</p>

                  <h1 style={styles.amountValue}>
                    {formatAmount(dashboardData.totalRecievedAmount)}
                  </h1>

                  <p style={styles.cardDescription}>View Payments</p>
                </div>
              </div>
            </div>
          )}

          {/* ================= API INFORMATION ================= */}

          {!loading && (
            <div style={styles.infoSection}>
              <div>
                <h3 style={styles.infoTitle}>Dashboard Summary</h3>

                <p style={styles.infoText}>
                  Data is fetched from the Application Admin dashboard API.
                </p>
              </div>

              <div style={styles.emailBox}>{appAdminEmailId}</div>
            </div>
          )}
        </div>
      </div>

      {/* ================= CSS ANIMATION ================= */}

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
}

export default ApplicationHeadDashboard;

/* ========================================================= */
/* ======================= STYLES ========================== */
/* ========================================================= */

const styles = {
  dashboard: {
    display: "flex",
    height: "100vh",
    background: "#F4F7FE",
    fontFamily: "Segoe UI, Arial, sans-serif",
    overflow: "hidden",
  },

  /* ================= SIDEBAR ================= */

  sidebar: {
    background: "#1E3A8A",
    color: "#fff",
    transition: "0.3s",
    overflow: "hidden",
    flexShrink: 0,
  },

  logo: {
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 10px",
    fontWeight: "bold",
    fontSize: 20,
    whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(255,255,255,.2)",
  },

  menu: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "17px",
    cursor: "pointer",
    transition: "0.3s",
    borderRadius: "10px",
    margin: "6px 10px",
    fontSize: 15,
    fontWeight: 500,
  },

  menuIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 24,
  },

  /* ================= MAIN ================= */

  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  /* ================= NAVBAR ================= */

  navbar: {
    height: 70,
    background: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 25px",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    flexShrink: 0,
  },

  left: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },

  navTitle: {
    margin: 0,
    fontSize: 20,
    color: "#1F2937",
  },

  right: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },

  userName: {
    fontWeight: 600,
    color: "#374151",
  },

  notification: {
    position: "relative",
    cursor: "pointer",
    color: "#374151",
  },

  badge: {
    position: "absolute",
    top: -8,
    right: -8,
    background: "#EF4444",
    color: "#fff",
    width: 18,
    height: 18,
    fontSize: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    fontWeight: "bold",
  },

  /* ================= CONTENT ================= */

  content: {
    padding: 30,
    overflowY: "auto",
    flex: 1,
  },

  headerSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
  },

  welcome: {
    margin: 0,
    color: "#111827",
    fontSize: 26,
  },

  description: {
    marginTop: 8,
    color: "#6B7280",
    fontSize: 15,
  },

  refreshButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "#2563EB",
    color: "#fff",
    padding: "11px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },

  /* ================= CARDS ================= */

  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 20,
    marginTop: 30,
  },

  card: {
    background: "#fff",
    padding: "22px",
    borderRadius: "15px",
    boxShadow: "0 5px 15px rgba(0,0,0,.08)",
    cursor: "pointer",
    transition: "all 0.25s ease",
    display: "flex",
    alignItems: "center",
    gap: 18,
    minHeight: 150,
    border: "1px solid #EEF2F7",
  },

  iconBox: {
    width: 55,
    height: 55,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  cardContent: {
    minWidth: 0,
  },

  cardTitle: {
    margin: 0,
    color: "#6B7280",
    fontSize: 14,
    fontWeight: 600,
  },

  cardValue: {
    margin: "7px 0",
    color: "#111827",
    fontSize: 30,
    fontWeight: 700,
  },

  amountValue: {
    margin: "7px 0",
    color: "#111827",
    fontSize: 25,
    fontWeight: 700,
  },

  cardDescription: {
    margin: 0,
    color: "#9CA3AF",
    fontSize: 12,
  },

  /* ================= LOADING ================= */

  loadingContainer: {
    marginTop: 50,
    background: "#fff",
    minHeight: 250,
    borderRadius: 15,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 5px 15px rgba(0,0,0,.08)",
    color: "#6B7280",
  },

  loader: {
    width: 35,
    height: 35,
    border: "4px solid #E5E7EB",
    borderTop: "4px solid #2563EB",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 15,
  },

  /* ================= INFO ================= */

  infoSection: {
    marginTop: 30,
    background: "#fff",
    borderRadius: 15,
    padding: 22,
    boxShadow: "0 5px 15px rgba(0,0,0,.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
  },

  infoTitle: {
    margin: 0,
    fontSize: 17,
    color: "#1F2937",
  },

  infoText: {
    margin: "6px 0 0",
    color: "#6B7280",
    fontSize: 13,
  },

  emailBox: {
    background: "#EFF6FF",
    color: "#1D4ED8",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: 13,
    fontWeight: 600,
  },
};
